/**
 * @fileoverview React Query hooks for Admin Projects page
 *
 * Provides data fetching and mutation hooks for admin project management.
 *
 * @requires @tanstack/react-query
 * @requires firebase/functions
 * @version 2.0.0
 */

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { functions } from "../firebase";
import { httpsCallable } from "firebase/functions";

// =============================================================================
// QUERY FUNCTIONS
// =============================================================================

/**
 * Fetch all projects for admin from Firebase
 * @param {Object} options - Filter options
 * @param {string} options.status - Filter by project status
 * @param {string} options.phase - Filter by construction phase
 * @param {string} options.type - Filter by project type
 * @param {boolean} options.showTestAccounts - Include test accounts (IT only)
 * @param {boolean} options.includeWarranty - Include warranty status projects
 * @returns {Promise<Array>} Array of project objects
 */
const fetchAllProjectsForAdmin = async (options = {}) => {
  const getAllProjectsForAdmin = httpsCallable(
    functions,
    "getAllProjectsForAdmin",
  );

  // Only pass non-default values to reduce payload
  const params = {};
  if (options.status && options.status !== "all") {
    params.status = options.status;
  }
  if (options.phase && options.phase !== "all") {
    params.phase = options.phase;
  }
  if (options.type && options.type !== "all") {
    params.type = options.type;
  }
  if (options.showTestAccounts) {
    params.showTestAccounts = true;
  }
  if (options.includeWarranty) {
    params.includeWarranty = true;
  }

  const result = await getAllProjectsForAdmin(params);

  if (!result.data.success) {
    throw new Error(result.data.message || "Failed to fetch projects");
  }

  return result.data.data;
};

/**
 * Fetch a single project for admin detail page
 * @param {string} projectId - Airtable project record ID
 * @returns {Promise<Object>} Project object
 */
const fetchProjectForAdmin = async (projectId) => {
  const getProjectForAdmin = httpsCallable(functions, "getProjectForAdmin");
  const result = await getProjectForAdmin({ projectId });

  if (!result.data.success) {
    throw new Error(result.data.message || "Failed to fetch project");
  }

  return result.data.data;
};

// =============================================================================
// QUERY HOOKS
// =============================================================================

/**
 * Hook to fetch all projects for Admin Projects page
 *
 * @param {Object} options - Filter options
 * @param {string} options.status - Filter by project status
 * @param {string} options.phase - Filter by construction phase
 * @param {string} options.type - Filter by project type
 * @param {boolean} options.showTestAccounts - Include test accounts (IT only)
 * @param {boolean} options.includeWarranty - Include warranty status projects
 * @returns {Object} React Query result object
 *
 * @example
 * const { data: projects, isLoading, error } = useAdminProjects();
 *
 * @example
 * // With filters
 * const { data: projects } = useAdminProjects({
 *   status: "Active Construction",
 *   type: "Custom Home",
 *   showTestAccounts: true
 * });
 */
export const useAdminProjects = (options = {}) => {
  return useQuery({
    queryKey: ["adminProjects", options],
    queryFn: () => fetchAllProjectsForAdmin(options),
    staleTime: 1000 * 60 * 5, // 5 minutes
    cacheTime: 1000 * 60 * 30, // 30 minutes
  });
};

/**
 * Hook to fetch a single project for Admin Project Detail page
 *
 * @param {string} projectId - Airtable project record ID
 * @returns {Object} React Query result object
 *
 * @example
 * const { data: project, isLoading, error } = useAdminProject(projectId);
 */
export const useAdminProject = (projectId) => {
  return useQuery({
    queryKey: ["adminProject", projectId],
    queryFn: () => fetchProjectForAdmin(projectId),
    enabled: !!projectId,
    staleTime: 1000 * 60 * 5, // 5 minutes
    cacheTime: 1000 * 60 * 30, // 30 minutes
  });
};

/**
 * Hook to fetch available project managers from the Team table
 * @returns {Object} Query object with data as array of {id, name, role}
 */
export const useProjectManagers = () => {
  return useQuery({
    queryKey: ["projectManagers"],
    queryFn: async () => {
      const getProjectManagersFn = httpsCallable(
        functions,
        "getProjectManagers",
      );
      const result = await getProjectManagersFn();
      return result.data?.data || [];
    },
    staleTime: 5 * 60 * 1000,
  });
};

// =============================================================================
// MUTATION HOOKS
// =============================================================================

/**
 * Hook to create a new project
 *
 * @returns {Object} Mutation object with mutate/mutateAsync functions
 *
 * @example
 * const createMutation = useCreateProject();
 * await createMutation.mutateAsync({
 *   projectName: "123 Main St",
 *   projectAddress: "123 Main St, Gainesville, GA",
 *   projectType: "Custom Home",
 *   projectStatus: "Pre-Construction",
 *   clientId: "recXXX",
 * });
 */
export const useCreateProject = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (projectData) => {
      const createProject = httpsCallable(functions, "createProject");
      const result = await createProject({ projectData });

      if (!result.data.success) {
        throw new Error(result.data.error || "Failed to create project");
      }

      return result.data.data;
    },
    onSuccess: () => {
      // Invalidate projects list to refetch
      queryClient.invalidateQueries(["adminProjects"]);
    },
  });
};

/**
 * Hook to update a project (any fields)
 *
 * @returns {Object} Mutation object with mutate/mutateAsync functions
 *
 * @example
 * const updateMutation = useUpdateProject();
 * await updateMutation.mutateAsync({
 *   projectId: "recXXX",
 *   updates: {
 *     projectStatus: "Active Construction",
 *     constructionPhase: "Framing",
 *     budget: 500000,
 *   }
 * });
 */
export const useUpdateProject = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ projectId, updates }) => {
      const updateProject = httpsCallable(functions, "updateProject");
      const result = await updateProject({ projectId, updates });

      if (!result.data.success) {
        throw new Error(result.data.error || "Failed to update project");
      }

      return result.data.data;
    },
    onSuccess: (data, variables) => {
      // Update the specific project in cache
      queryClient.setQueryData(["adminProject", variables.projectId], data);
      // Invalidate projects list to refetch
      queryClient.invalidateQueries(["adminProjects"]);
    },
  });
};

/**
 * Hook to delete a project attachment
 *
 * @returns {Object} Mutation object with mutate/mutateAsync functions
 *
 * @example
 * const deleteMutation = useDeleteProjectAttachment();
 * await deleteMutation.mutateAsync({
 *   projectId: "recXXX",
 *   field: "floorplans",
 *   attachmentId: "attXXX",
 * });
 */
export const useDeleteProjectAttachment = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ projectId, field, attachmentId }) => {
      const deleteProjectAttachment = httpsCallable(
        functions,
        "deleteProjectAttachment",
      );
      const result = await deleteProjectAttachment({
        projectId,
        field,
        attachmentId,
      });

      if (!result.data.success) {
        throw new Error(result.data.error || "Failed to delete attachment");
      }

      return result.data.data;
    },
    onSuccess: (data, variables) => {
      // Invalidate plan documents query
      queryClient.invalidateQueries(["plan-documents", variables.projectId]);
      // Also invalidate project detail in case it shows attachments
      queryClient.invalidateQueries(["adminProject", variables.projectId]);
    },
  });
};

export default useAdminProjects;
